'use client';
import { useEffect, useMemo } from 'react';

import { UseQueryOptions, useMutation } from '@tanstack/react-query';

import {
  ApiError,
  BasketViewModel,
  PaymentRequestChannelType,
} from '@api/index';
import {
  getBasketFromCache,
  useBasketId as getBasketIdFromCache,
  useBasketId,
} from '@hooks/cache';
import { checkAdvancePaymentSupport, clearBasketStorage } from '@utils/basket';
import { callService } from '@api/caller';
import { checkIsCallCenterPayment } from '@utils/checkIsCallCenterPayment';

import useQueryWithCallbacks from './useQueryWithCallbacks';

export const useBasketMutation = () => {
  const { mutate } = useMutation(
    (basketId: string) => {
      return callService('BasketsService', 'getBasket', { basketId });
    },
    {
      onSuccess: (basketInfo) => {
        if (typeof window !== 'undefined') {
          localStorage.setItem('basketDetail', JSON.stringify(basketInfo));
          localStorage.setItem('basketId', basketInfo?.id);
        }
      },
      onError(e) {
        const { detail, title } = (e as ApiError).body?.errors?.[0] ?? {};

        if (
          /bulunama/.test(detail) ||
          /Yetkisiz/.test(detail) ||
          401 === +title ||
          404 === +title
        ) {
          clearBasketStorage(true);
        }
      },
    }
  );

  return { mutate };
};

export const useBasketQuery = (
  basketId?: string | string[],
  options?: UseQueryOptions<BasketViewModel>
) => {
  const cacheBasketId = useBasketId();
  const bid =
    basketId && Array.isArray(basketId)
      ? basketId[0]
      : basketId || cacheBasketId || '';

  useEffect(() => {
    if (bid && cacheBasketId && bid !== cacheBasketId) {
      localStorage.setItem('basketId', bid as string);
    }
  }, [bid, cacheBasketId]);

  const fetchBasket = async () =>
    await callService('BasketsService', 'getBasket', {
      basketId: bid as string,
    });

  // Separate queryFn from options to avoid type conflicts
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { queryFn, ...restOptions } = options || {};

  return useQueryWithCallbacks({
    queryKey: ['basketDetail', bid],
    queryFn: fetchBasket,
    enabled: Boolean(bid),
    onSuccess: (basketInfo) => {
      if (typeof window !== 'undefined') {
        localStorage.setItem('basketDetail', JSON.stringify(basketInfo));
        localStorage.setItem('basketId', bid as string);
      }
    },
    onError: (e) => {
      const { detail, title } = (e as ApiError).body?.errors?.[0] ?? {};
      if (
        /bulunama/.test(detail) ||
        /Yetkisiz/.test(detail) ||
        401 === +title ||
        404 === +title
      ) {
        clearBasketStorage(true);
      }
    },
    ...restOptions,
  });
};
/**
 * Returns basket details.
 * If it is a call center payment, it clears the storage and returns the basket details according to the current basket id.
 * @param bid = string => basket id
 * @param paymentRequestChannel = PaymentRequestChannelType => 'ShopAndFlyWeb' | 'ShopAndFlyCallCenter' | 'ShopAndFlyCallCenterManualPayment'
 * @returns object
 */
const useBasketInformation = (
  bid?: string,
  paymentRequestChannel: PaymentRequestChannelType = PaymentRequestChannelType[
    'SHOP_AND_FLY_WEB'
  ],
  options?: UseQueryOptions<BasketViewModel>
) => {
  const isCallCenterPayment = checkIsCallCenterPayment(paymentRequestChannel);
  isCallCenterPayment && clearBasketStorage();
  const basketId = bid ?? getBasketIdFromCache();
  const { data: basketInfo, ...rest } = useBasketQuery(basketId, options);
  const basketFromCache = getBasketFromCache();

  isCallCenterPayment && clearBasketStorage();
  const basket = basketInfo ?? basketFromCache;
  const itemsInBasket = useMemo(() => {
    if (!basket) return 0;
    const allItems: number[] = [];

    const { hotels, flights, tours, vehicle } = basket;

    const vehicleAsArray = vehicle ? [vehicle] : [];

    [hotels, flights, tours, vehicleAsArray].forEach((item) => {
      if (item && Array.isArray(item) && item.length) {
        allItems.push((item ?? []).length || 0);
      }
    });

    const count = allItems.reduce((partialSum, a) => partialSum + a, 0);

    return count;
  }, [basket]);

  return {
    basketInfo: basket,
    itemsInBasket,
    basketId,
    supportAdvancePayment: checkAdvancePaymentSupport(basket),
    hasVehicleInBasket: Boolean(basket?.vehicle),
    ...rest,
  };
};
export { getBasketFromCache };

export default useBasketInformation;
